/* eslint-disable import/prefer-default-export */
import axios from '../../axios';

// ---------------------------------------------------------------------------
// RH / Recrutamento & Seleção — Candidatos (service layer, handoff 0061).
//
// Camada fina sobre o axios singleton. Desembrulha o envelope JSend
// (`response.data.data`) e renomeia campos snake_case → camelCase apenas nas
// FRONTEIRAS (paginação). Os campos de registro (full_name, stage, etc.) ficam
// em snake_case para preservar simetria com o backend.
//
// Tratamento de erro NÃO acontece aqui — é responsabilidade das páginas
// (toast / fieldErrors, via readApiError). Recurso: `/hr/recruitment/candidates`
// (permissões hr:candidate:*). `stage`/`final_outcome` são READ-ONLY: nunca vão
// no body de create/update — só mudam via puxar-pra-vaga e resultado de
// entrevista.
// ---------------------------------------------------------------------------

export async function listCandidates(filters = {}) {
   // Todos os filtros são opcionais e combinados com AND no backend; só envia os
   // não-vazios. `onlyPool=true` → só o banco de currículos (candidatos sem
   // vaga). A tradução camelCase → snake_case acontece SÓ aqui (boundary).
   const {
      page,
      perPage,
      search,
      stage,
      jobOpeningId,
      desiredPositionId,
      originId,
      onlyPool,
   } = filters;

   const params = {};
   if (page) params.page = page;
   if (perPage) params.per_page = perPage;
   if (search) params.search = search;
   if (stage) params.stage = stage;
   if (jobOpeningId) params.job_opening_id = jobOpeningId;
   if (desiredPositionId) params.desired_position_id = desiredPositionId;
   if (originId) params.origin_id = originId;
   if (onlyPool) params.only_pool = true;

   const response = await axios.get('/hr/recruitment/candidates', { params });
   const envelope = response?.data ?? {};
   const data = envelope.data ?? {};
   const meta = envelope.meta ?? {};

   return {
      candidates: data.candidates ?? [],
      total: meta.total ?? 0,
      page: meta.page ?? 1,
      perPage: meta.per_page ?? 20,
      totalPages: meta.total_pages ?? 1,
   };
}

// Detalhe — inclui `interviews[]` (ordenadas por created_at).
export async function getCandidate(candidateId) {
   const response = await axios.get(`/hr/recruitment/candidates/${candidateId}`);
   return response?.data?.data?.candidate ?? null;
}

export async function createCandidate(payload) {
   const response = await axios.post('/hr/recruitment/candidates', payload);
   return {
      candidate: response?.data?.data?.candidate ?? null,
      message: response?.data?.message ?? 'Candidato cadastrado com sucesso.',
   };
}

export async function updateCandidate(candidateId, payload) {
   const response = await axios.put(
      `/hr/recruitment/candidates/${candidateId}`,
      payload,
   );
   return {
      candidate: response?.data?.data?.candidate ?? null,
      message: response?.data?.message ?? 'Candidato atualizado com sucesso.',
   };
}

// Backend responde 204 sem body (barra com 409 se houver entrevistas).
export async function deleteCandidate(candidateId) {
   await axios.delete(`/hr/recruitment/candidates/${candidateId}`);
}

// Puxa o candidato pra esteira de uma vaga ABERTA (BANCO_CURRICULOS →
// ENTREVISTA_1). O stage muda no backend.
export async function pullCandidateToOpening(candidateId, jobOpeningId) {
   const response = await axios.patch(
      `/hr/recruitment/candidates/${candidateId}/opening`,
      { job_opening_id: jobOpeningId },
   );
   return {
      candidate: response?.data?.data?.candidate ?? null,
      message: response?.data?.message ?? 'Candidato puxado para a vaga.',
   };
}

// Currículo — upload multipart (campo `file`; PDF/JPEG/PNG/WEBP, ≤10MB).
export async function uploadResume(candidateId, file) {
   const formData = new FormData();
   formData.append('file', file);

   const response = await axios.post(
      `/hr/recruitment/candidates/${candidateId}/resume`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
   );
   return {
      candidate: response?.data?.data?.candidate ?? null,
      message: response?.data?.message ?? 'Currículo enviado com sucesso.',
   };
}

// Currículo — download: retorna { url, filename } (URL S3 presigned, ~15min).
// A página abre a URL em nova aba (window.open).
export async function getResumeDownload(candidateId) {
   const response = await axios.get(
      `/hr/recruitment/candidates/${candidateId}/resume`,
   );
   const data = response?.data?.data ?? {};
   return {
      url: data.url ?? null,
      filename: data.filename ?? null,
   };
}
